import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { DetailCard } from '@/components/common/DetailCard';
import { DetailField } from '@/components/common/DetailField';
import { JobFinancialSummary } from './JobFinancialSummary';
import type { JobDetailDto } from '../types';

interface JobOverviewTabProps {
  job: JobDetailDto;
  onEditClick: () => void;
}

export function JobOverviewTab({ job, onEditClick }: JobOverviewTabProps) {
  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPercent = (percent: number) => {
    return `${percent.toFixed(2)}%`;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
      {/* Left Column - Job Details Card */}
      <DetailCard
        title="Job Details"
        titleAction={
          <Button
            onClick={onEditClick}
            variant="outline"
            size="sm"
            className="rounded-[50px] text-xs"
          >
            Edit
          </Button>
        }
      >
        <div className="space-y-3">
          <DetailField label="Start Date" value={formatDate(job.startDate)} emptyText="Not set" />

          {job.completedDate && (
            <DetailField label="Completed Date" value={formatDate(job.completedDate)} />
          )}

          {job.closedDate && (
            <DetailField label="Closed Date" value={formatDate(job.closedDate)} />
          )}

          <DetailField label="Description" emptyText="No description">
            {job.description && (
              <span className="whitespace-pre-wrap">{job.description}</span>
            )}
          </DetailField>

          <DetailField label="Notes" emptyText="No notes">
            {job.notes && (
              <span className="whitespace-pre-wrap">{job.notes}</span>
            )}
          </DetailField>

          {job.closingComments && (
            <DetailField label="Closing Comments">
              <span className="whitespace-pre-wrap">{job.closingComments}</span>
            </DetailField>
          )}
        </div>
      </DetailCard>

      {/* Right Column - Lead Summary and Financial Summary */}
      <div className="space-y-4">
        {/* Lead Summary Card */}
        <DetailCard title="Lead Information">
          <div className="space-y-3">
            <DetailField label="Customer Name">
              <Link
                to={`/leads/${job.leadId}`}
                className="font-medium text-[#0A0A9E] hover:underline"
              >
                {job.lead.customerName}
              </Link>
            </DetailField>

            <DetailField label="Property Address" value={job.lead.propertyAddress} />

            {job.lead.insuranceCompany && (
              <DetailField label="Insurance Company" value={job.lead.insuranceCompany} />
            )}

            {job.lead.claimNumber && (
              <DetailField label="Claim Number" value={job.lead.claimNumber} />
            )}

            {job.lead.assignedTo && (
              <DetailField label="Assigned Estimator" value={job.lead.assignedTo} />
            )}

            <DetailField label="Commission Rate">
              <span className="font-medium">{formatPercent(job.lead.commissionPercent)}</span>
            </DetailField>
          </div>
        </DetailCard>

        {/* Financial Summary Card */}
        <JobFinancialSummary financial={job.financial} />
      </div>
    </div>
  );
}
